import { IsNode, IsWorker } from "@pdfking/compatibility"
import {
  type PdfiumApi,
  PdfiumExports as E,
  type PdfiumTypeMap,
  type Pointer,
} from "@pdfking/pdfium-wrap"
import type { PDFReaderOptions } from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"
import {
  BlobToUint8,
  Constant,
  IsNull,
  IterableToArray,
  MapAsync,
  ResizeBlobs,
  ToBlob,
} from "@pdfking/tfunc"

type PointerAvail = PdfiumTypeMap["FPDF_AVAIL"]
type PointerDoc = PdfiumTypeMap["FPDF_DOCUMENT"]

const PDF_DATA_NOTAVAIL = 0
const PDF_DATA_AVAIL = 1

declare const FileReaderSync: {
  new (): { readAsArrayBuffer(blob: Blob): ArrayBuffer }
}

const read_blob_sync_worker = (blob: Blob): Uint8Array =>
  new Uint8Array(new FileReaderSync().readAsArrayBuffer(blob))

const read_blob_sync_xhr = (blob: Blob): Uint8Array => {
  const url = URL.createObjectURL(blob)
  try {
    const xhr = new XMLHttpRequest()
    xhr.open("GET", url, false)
    if (xhr.overrideMimeType) {
      xhr.overrideMimeType("text/plain; charset=x-user-defined")
    }
    xhr.send()
    const response = xhr.response
    const result = new Uint8Array(response.length)
    for (let j = 0; j < response.length; j++) {
      result[j] = response.charCodeAt(j) & 0xff
    }
    return result
  } finally {
    URL.revokeObjectURL(url)
  }
}

const read_blob_sync = IsWorker() ? read_blob_sync_worker : read_blob_sync_xhr

const is_data_available = (
  blobs_array: Record<number, Blob>,
  chunk_size: number,
  offset: number,
  size: number
) => {
  const start_chunk = Math.floor(offset / chunk_size)
  const end_chunk = Math.floor((offset + size - 1) / chunk_size)

  for (let i = start_chunk; i <= end_chunk; i++) {
    if (IsNull(blobs_array[i])) {
      return false
    }
  }
  return true
}

const get_data_from_blobs = (
  blobs_array: Record<number, Blob>,
  chunk_size: number,
  offset: number,
  size: number
) => {
  const result = new Uint8Array(size)
  let result_offset = 0

  const start_chunk = Math.floor(offset / chunk_size)
  const end_chunk = Math.floor((offset + size - 1) / chunk_size)
  const start_offset = offset % chunk_size

  for (let i = start_chunk; i <= end_chunk; i++) {
    const chunk_blob = blobs_array[i]
    if (!chunk_blob) {
      throw new Error(`Chunk ${i} not in blobs array`)
    }

    const chunk_start = i === start_chunk ? start_offset : 0
    let chunk_end = chunk_blob.size

    if (i === end_chunk) {
      const remaining = size - result_offset
      chunk_end = Math.min(chunk_start + remaining, chunk_blob.size)
    }

    const chunk_data = read_blob_sync(chunk_blob.slice(chunk_start, chunk_end))
    result.set(chunk_data, result_offset)
    result_offset += chunk_data.length
  }

  return result
}

export type ReadDocumentResult = {
  doc_pointer: PointerDoc
  cleanup_functions: Array<() => void>
  blobs_array: Record<number, Blob>
  size: number
  file_pointer: null | number
  avail: null | PointerAvail
  hints_ptr: null | number
  promise_generator: AsyncGenerator<Blob> | null
}

export const ReadDocument = async (
  api: PdfiumApi,
  options: PDFReaderOptions
): Promise<ReadDocumentResult> => {
  const {
    call,
    malloc,
    setInt32,
    writeBytes,
    writeTo,
    addCallback,
    writeString,
  } = api
  const { stream, size, password = "" } = options

  const chunk_size = (1024 * 1024 * 30) as Bytes

  const { pointer: passwordPtr, free: freePassword } = writeString(password)

  const cleanup_functions: Array<() => void> = [freePassword]

  if (IsNode() || size <= chunk_size) {
    const blobs = await IterableToArray(stream)
    const uint8Array = await BlobToUint8(ToBlob(blobs))

    const { pointer: dataPtr, free: freeData } = writeBytes(uint8Array)
    cleanup_functions.push(freeData)

    const doc_pointer = await call(
      E.FPDF_LoadMemDocument,
      dataPtr,
      uint8Array.length,
      passwordPtr
    )

    return {
      doc_pointer,
      cleanup_functions,
      blobs_array: {},
      size,
      file_pointer: null,
      avail: null,
      hints_ptr: null,
      promise_generator: null,
    }
  }

  const blobs_array: Record<number, Blob> = {}

  const promise_generator = MapAsync(
    (blob, chunk_index) => (blobs_array[chunk_index] = blob),
    ResizeBlobs(stream, chunk_size)
  )

  const isDataAvail = (_pThis: number, offset: number, size: number) =>
    is_data_available(blobs_array, chunk_size, offset, size) ? 1 : 0

  const { pointer: isDataAvailPtr, free: freeIsDataAvail } = addCallback(
    isDataAvail,
    "iiii"
  )
  cleanup_functions.push(freeIsDataAvail)

  const { pointer: fileAvailPtr, free: freeFileAvail } = malloc(8 as Bytes)
  cleanup_functions.push(freeFileAvail)

  setInt32(fileAvailPtr, 1)
  setInt32((fileAvailPtr + 4) as Pointer, isDataAvailPtr)

  const addSegment = Constant(0)

  const { pointer: addSegmentPtr, free: freeAddSegment } = addCallback(
    addSegment,
    "viii"
  )
  cleanup_functions.push(freeAddSegment)

  const { pointer: hints_ptr, free: freeHints } = malloc(8 as Bytes)
  cleanup_functions.push(freeHints)

  setInt32(hints_ptr, 1)
  setInt32((hints_ptr + 4) as Pointer, addSegmentPtr)

  const getBlock = (
    _param: number,
    position: number,
    pBuf: number,
    size: number
  ) => {
    if (!is_data_available(blobs_array, chunk_size, position, size)) {
      return 0
    }

    const data = get_data_from_blobs(blobs_array, chunk_size, position, size)
    writeTo(pBuf as Pointer, data)
    return 1
  }

  const { pointer: getBlockPtr, free: freeGetBlock } = addCallback(
    getBlock,
    "iiiii"
  )
  cleanup_functions.push(freeGetBlock)

  const { pointer: file_pointer, free: freeFileAccess } = malloc(12 as Bytes)
  cleanup_functions.push(freeFileAccess)

  setInt32(file_pointer, size)
  setInt32((file_pointer + 4) as Pointer, getBlockPtr)
  setInt32((file_pointer + 8) as Pointer, 0)

  const avail = await call(E.FPDFAvail_Create, fileAvailPtr, file_pointer)
  cleanup_functions.push(() => void call(E.FPDFAvail_Destroy, avail))

  let doc_pointer: null | PointerDoc = null
  let avail_done = false

  while (!avail_done) {
    const doc_status = await call(E.FPDFAvail_IsDocAvail, avail, hints_ptr)

    if (doc_status === PDF_DATA_AVAIL) {
      avail_done = true
    } else if (doc_status === PDF_DATA_NOTAVAIL) {
      const { done } = await promise_generator.next()
      avail_done = done ?? false
    } else {
      throw new Error(`Document loading error: status ${doc_status} source`)
    }
  }

  doc_pointer = await call(E.FPDFAvail_GetDocument, avail, passwordPtr)

  return {
    doc_pointer,
    file_pointer,
    cleanup_functions,
    avail,
    hints_ptr,
    blobs_array,
    promise_generator,
    size,
  }
}
